import logoBT from "@assets/logo-masthead.svg";
import logoUOB from "@assets/logo-uob-blue.png";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import BasicCard from "@components/ArticleCard/BasicCard";
import { CardVariations } from "@components/ArticleCard/types";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import MetaTags from "@components/MetaTags/MetaTags";
import EventsAwardsLogoMenu from "@events-awards/components/EventsAwardsLogoMenu";
import {
  EVENTS_AWARDS_SIA_LABEL,
  EVENTS_AWARDS_SIA_PATH,
  EVENTS_AWARDS_SLOT,
} from "@events-awards/utils/constants";
import {
  AwardType,
  EventsAwardsLandingPageContextData,
} from "@events-awards/utils/types";
import { sectionNavigationItems } from "@util/constant";
import { getKickerFromArticleDataObject } from "@util/helpers";
import { useDetectAdBlock } from "adblock-detect-react";

import SIAFooter from "./components/SIAFooter";

/**
 * Component to render event and awards page
 */
const EventsAwardsSIAPageContent = ({
  title,
  article,
}: EventsAwardsLandingPageContextData): React.ReactElement => {
  const { isShowBreadCrumb } = useBreadCrumbState();
  const adBlockDetected = useDetectAdBlock();

  const variant: CardVariations = {
    title: {
      color: "text-gray-850",
      size: "text-6xl",
      extraClass: "font-public-sans font-semibold",
    },
    image: {
      width: "w-full  lg:w-1/2",
      position: "left",
      aspectRatio: "object-cover",
    },
    content: {
      width: "w-full lg:w-1/2",
      extraClass: "px-6 lg:px-14 text-left",
      blurb: {
        extraClass: "text-gray-750",
      },
    },
  };

  return (
    <div
      className="w-full"
      data-testid="events-awards-sia-page-content-component"
    >
      <AdSettings
        adNames={["lb1", "prestitial", "catfish", "abm"]}
        path={EVENTS_AWARDS_SIA_PATH}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        title="Sustainability Impact Awards"
        level2="events awards"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <MetaTags
        title={title}
        description={`THE BUSINESS TIMES ${EVENTS_AWARDS_SIA_LABEL} - Find ${EVENTS_AWARDS_SIA_LABEL} News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times.`}
        ogType="website"
        slug={EVENTS_AWARDS_SIA_PATH}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        displayBreadcrumb={isShowBreadCrumb}
        parentCategory={EVENTS_AWARDS_SLOT}
        categoryDataMapping={sectionNavigationItems}
        enableTopStoriesLink={false}
      />

      <Row rootClassName="justify-center">
        <Column rootClassName="w-full lg:w-8/12 text-center">
          <EventsAwardsLogoMenu awardType={AwardType.SIA} />
        </Column>
      </Row>

      {article ? (
        <Row rootClassName="justify-center bg-gray-150">
          <Column rootClassName="w-full lg:w-8/12 text-center">
            <Row rootClassName="justify-center mb-5">
              <BasicCard
                title={article.title}
                id={article.id}
                media={article.media}
                slug={article.urlPath}
                blurb={article.blurb}
                kicker={getKickerFromArticleDataObject(article)}
                defaultImage={{ directoryName: "events-awards" }}
                rootClassName="mt-6 mx-0 justify-center"
                variations={variant}
              />
            </Row>
          </Column>
        </Row>
      ) : null}

      <Row rootClassName="justify-center">
        <Column rootClassName="w-full lg:w-8/12 text-center">
          <Row rootClassName="flex py-9 px-3 md:px-6 lg:px-0">
            <Column rootClassName="w-full lg:w-4/12 lg:pr-7">
              <h2 className="leading-1 mb-6 text-left font-public-sans text-4xl font-medium lg:mb-0 lg:text-8xl">
                {"Sustainability "}
                <br className="hidden lg:inline" />
                {"Impact Awards"}
              </h2>
            </Column>

            <Column rootClassName="w-full lg:w-8/12 text-left lg:pl-7">
              <p className="text-md font-public-sans leading-7">
                {
                  "The Sustainability Impact Awards (SI Awards), launched on 1 February 2023, is jointly presented by The Business Times and UOB, with the Centre for Governance and Sustainability (CGS) at the National University of Singapore Business School as the knowledge partner."
                }
              </p>
            </Column>
          </Row>
        </Column>
      </Row>

      <Row rootClassName="flex py-9 px-3 md:px-6 lg:px-0 justify-center items-center bg-gray-150 mt-1">
        <Column rootClassName="w-full lg:w-8/12 text-center flex flex-col items-center">
          <IconFactory type={IconTypeEnum.apply} x={80} y={60} size={80} />

          <h2 className="text-center align-middle font-public-sans text-4xl lg:text-8xl">
            Nominations for SI Awards 2025 are closed
          </h2>
        </Column>
      </Row>

      <Row rootClassName="justify-center">
        <Column rootClassName="w-full lg:w-8/12 text-center mb-8">
          <Row rootClassName="py-9 px-3 md:px-6 lg:px-0 justify-center">
            <Column rootClassName="lg:w-9/12 text-left lg:pl-7 justify-center">
              <BlockTitle
                text="Jointly Organised By"
                rootClassName="text-center mb-2"
              />

              <Row rootClassName="justify-center items-center">
                <Column>
                  <img
                    src={logoBT}
                    width={211}
                    alt="The Business Times logo"
                    className="mx-16 my-6"
                  />
                </Column>

                <Column>
                  <img
                    src={logoUOB}
                    width={141}
                    alt="UOB logo"
                    className="mx-16 my-6"
                  />
                </Column>
              </Row>
            </Column>
          </Row>

          <Row rootClassName="text-center py-3 text-gray-850">
            <Column rootClassName="w-full py-3 lg:w-1/3 lg:py-0">
              <p>
                <strong>Dr Amy Khor</strong>
                <br />
                Chairman of the Judging Panel
              </p>
            </Column>

            <Column rootClassName="w-full py-3 lg:w-1/3 lg:py-0">
              <p>
                <strong>Ms Chen Huifen</strong>
                <br />
                Editor
                <br />
                The Business Times
              </p>
            </Column>

            <Column rootClassName="w-full py-3 pb-0 lg:w-1/3 lg:py-0">
              <p>
                <strong>Mr Geoffrey Yeo</strong>
                <br />
                Assistant Managing Director, Capabilities, Urban
                <br />
                Systems &amp; Solutions, Enterprise Singapore
              </p>
            </Column>
          </Row>

          <Row rootClassName="text-center py-3 text-gray-850">
            <Column rootClassName="w-full py-3 lg:w-1/3 lg:py-0">
              <p>
                <strong>Professor Lawrence Loh</strong>
                <br />
                Director, Centre for Governance and Sustainability
                <br />
                Professor, Strategy and Policy
                <br />
                NUS Business School
                <br />
                National University of Singapore
              </p>
            </Column>

            <Column rootClassName="w-full py-3 lg:w-1/3 lg:py-0">
              <p>
                <strong>Ms Eliza Tan</strong>
                <br />
                Senior Vice President
                <br />
                Head, IPO Admissions
                <br />
                Head, Sustainable Development Office
              </p>
            </Column>

            <Column rootClassName="w-full py-3 pb-0 lg:w-1/3 lg:py-0">
              <p>
                <strong>Mr Tan Swee Yiow</strong>
                <br />
                President of REDAS
                <br />
                Real Estate Developers&apos;s Association of Singapore
                <br />
                (REDAS)
              </p>
            </Column>
          </Row>

          <Row rootClassName="text-center justify-center py-3 text-gray-850">
            <Column rootClassName="w-full lg:w-1/3">
              <p>
                <strong>Ms Theresa Goh</strong>
                <br />
                Managing Founder
                <br />
                360 Dynamics Limited
              </p>
            </Column>
          </Row>
        </Column>
      </Row>

      <SIAFooter />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsSIAPageContent;
